import {
  Badge,
  ButtonLink,
  PlusIcon,
  Stack,
  Table,
  TableCell,
  TablePager,
  TableRow,
} from '@fukuri-admins/ui/common';
import { getCreatePushNotificationLink } from '~/utils/getLinks';
import styles from './styles.module.scss';

/**
 * プッシュ通知一覧テーブルの行データ型
 *
 * 表示整形済みの値を受け取る（converter で API 型から変換される）
 */
type NotificationRow = {
  /** プッシュ通知の一意な識別子（API の notification.id） */
  id: number;
  /** タイトル（25文字を超える場合は末尾を「...」で省略済み） */
  title: string;
  /** 本文（10文字を超える場合は末尾を「...」で省略済み） */
  body: string;
  /** 遷移先URL（未設定の場合は '-'、25文字を超える場合は末尾を「...」で省略済み） */
  linkUrl: string;
  /** 配信先グループ名（未設定の場合は '-'） */
  groupName: string;
  /** 対象ユーザー数（0 件の場合は '-'、表示整形済み） */
  targetUserCount: string;
  /** 配信日時（表示整形済み） */
  deliverAt: string;
  /** 配信済みか（true: 配信完了、false: 配信予約） */
  isDelivered: boolean;
};

type Props = {
  /** テーブルに表示するプッシュ通知行データ */
  rows: NotificationRow[];
  /** ページャー props */
  pagerProps: React.ComponentProps<typeof TablePager>;
};

/**
 * プッシュ通知一覧 Presentation
 *
 * - 検索フィールドはなし。上段右側に新規作成ボタンのみ配置する
 * - Table は fillHeight + size='md' でビューポートの残り高さに合わせて可変にする
 * - 配信状況列は deliver_at が現在時刻以前かどうかで「配信完了」「配信予約」の Badge を出し分ける
 */
export const PushNotificationListPresentation: React.FC<Props> = ({ rows, pagerProps }) => (
  <Stack direction='column' gap={16}>
    <Stack direction='row' justifyContent='flex-end'>
      <ButtonLink
        variant='primary'
        size='md'
        icon={<PlusIcon size='xs' />}
        iconPosition='left'
        to={getCreatePushNotificationLink()}
      >
        新規作成
      </ButtonLink>
    </Stack>
    <Table
      fillHeight
      size='md'
      scrollable
      headers={[
        { label: '配信状況', className: styles.statusCol },
        { label: 'タイトル' },
        { label: '本文' },
        { label: 'URL' },
        { label: '配信先' },
        {
          label: '対象ユーザー数',
          className: styles.targetUserCountCol,
        },
        {
          label: '配信日時',
          className: styles.deliverAtCol,
        },
      ]}
    >
      {rows.map(row => (
        <TableRow key={row.id}>
          <TableCell className={styles.statusCol}>
            <Badge
              status={row.isDelivered ? 'success' : 'neutral'}
              variant='subtle'
              size='sm'
              label={row.isDelivered ? '配信完了' : '配信予約'}
            />
          </TableCell>
          <TableCell>{row.title}</TableCell>
          <TableCell>{row.body}</TableCell>
          <TableCell>{row.linkUrl}</TableCell>
          <TableCell>{row.groupName}</TableCell>
          <TableCell className={styles.targetUserCountCol}>{row.targetUserCount}</TableCell>
          <TableCell className={styles.deliverAtCol}>{row.deliverAt}</TableCell>
        </TableRow>
      ))}
    </Table>
    <TablePager {...pagerProps} />
  </Stack>
);
